import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Box, Typography, TextField } from "@mui/material";
import { validateContactPreferenceField } from "./contactpreferences.helper";

const ContactPreferences = ({
  data,
  onDataChange,
  onValidationChange,
  showErrors,
}) => {
  const [formData, setFormData] = useState(data);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    // Initial validation
    const fields = [
      "contactMethod",
      "refillContactMethod",
      "quantity",
      "daysSupply",
    ];
    const initialErrors = {};
    fields.forEach((field) => {
      const error = validateContactPreferenceField(field, formData[field]);
      if (error) initialErrors[field] = error;
    });
    setErrors(initialErrors);

    // If data was empty, sync back if needed (but parent already has defaults for these)
  }, [formData]);

  const validateField = (name, value) => {
    const error = validateContactPreferenceField(name, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      onDataChange(next);
      return next;
    });
    validateField(name, value);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    const shouldShow = touched[name] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[name] : "",
    };
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 3, height: "100%" }}
    >
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "12px",
          p: 2,
          border: "1px solid #E8E8E9",
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Typography
          sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
        >
          Order Details
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Quantity
          </Typography>
          <TextField
            name="quantity"
            type="number"
            slotProps={{ input: { min: 0 } }}
            variant="outlined"
            size="small"
            value={formData.quantity}
            onChange={handleChange}
            onBlur={handleBlur}
            {...getErrorProps("quantity")}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("quantity").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Days Supply
          </Typography>
          <TextField
            name="daysSupply"
            type="number"
            slotProps={{ input: { min: 0 } }}
            variant="outlined"
            size="small"
            value={formData.daysSupply}
            onChange={handleChange}
            onBlur={handleBlur}
            {...getErrorProps("daysSupply")}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("daysSupply").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

ContactPreferences.propTypes = {
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
};

export default ContactPreferences;
